import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Activity, 
  Cpu, 
  Database, 
  HardDrive, 
  Network, 
  Clock, 
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Wifi
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { productionLogger } from '@/utils';
import { toast } from 'sonner';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface SystemHealth {
  cpu: number;
  memory: number;
  disk: number;
  network: number;
  database: {
    connections: number;
    queries_per_second: number;
    avg_query_time: number;
    cache_hit_ratio: number;
  };
  response_times: {
    api: number;
    database: number;
    static: number;
  };
  errors: {
    total_5m: number;
    rate: number;
    last_error?: string;
  };
}

interface ServiceStatus {
  name: string;
  status: 'healthy' | 'warning' | 'error';
  response_time: number;
  uptime: number;
  last_check: string;
  error?: string;
}

interface RealtimeMetric {
  timestamp: string;
  cpu: number;
  memory: number;
  requests_per_minute: number;
  active_connections: number;
  error_count: number;
}

export function RealtimeSystemMonitor() {
  const [systemHealth, setSystemHealth] = useState<SystemHealth>({
    cpu: 0,
    memory: 0,
    disk: 0,
    network: 0,
    database: {
      connections: 0,
      queries_per_second: 0,
      avg_query_time: 0,
      cache_hit_ratio: 0,
    },
    response_times: {
      api: 0,
      database: 0,
      static: 0,
    },
    errors: {
      total_5m: 0,
      rate: 0,
    },
  });

  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [realtimeData, setRealtimeData] = useState<RealtimeMetric[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);

  // Generate mock system data (in production, this would come from actual monitoring)
  const generateMockSystemData = useCallback((): SystemHealth => {
    return {
      cpu: Math.random() * 100,
      memory: 60 + Math.random() * 30,
      disk: 45 + Math.random() * 20,
      network: Math.random() * 100,
      database: {
        connections: Math.floor(10 + Math.random() * 50),
        queries_per_second: Math.floor(50 + Math.random() * 200),
        avg_query_time: 10 + Math.random() * 50,
        cache_hit_ratio: 85 + Math.random() * 10,
      },
      response_times: {
        api: 50 + Math.random() * 200,
        database: 20 + Math.random() * 100,
        static: 10 + Math.random() * 50,
      },
      errors: {
        total_5m: Math.floor(Math.random() * 10),
        rate: Math.random() * 2,
      },
    };
  }, []);

  const fetchSystemHealth = useCallback(async () => {
    try {
      // In a real implementation, this would fetch from monitoring APIs
      // For now, we'll use mock data combined with real metrics where available
      
      const mockData = generateMockSystemData();
      
      // Get real performance metrics if available
      const { data: perfMetrics, error: perfError } = await supabase
        .from('performance_metrics')
        .select('*')
        .gte('recorded_at', new Date(Date.now() - 5 * 60 * 1000).toISOString())
        .order('recorded_at', { ascending: false });

      if (!perfError && perfMetrics?.length) {
        const avgApiTime = perfMetrics
          .filter(m => m.metric_type === 'api_request')
          .reduce((acc, m) => acc + Number(m.duration_ms), 0) / perfMetrics.length || mockData.response_times.api;
        
        const errorCount = perfMetrics.filter(m => m.status === 'error').length;

        mockData.response_times.api = avgApiTime;
        mockData.errors.total_5m = errorCount;
        mockData.errors.rate = (errorCount / perfMetrics.length) * 100;
      }

      setSystemHealth(mockData);

      // Update services status
      const servicesData: ServiceStatus[] = [
        {
          name: 'Database',
          status: mockData.database.avg_query_time < 100 ? 'healthy' : 'warning',
          response_time: mockData.database.avg_query_time,
          uptime: 99.9,
          last_check: new Date().toISOString(),
        },
        {
          name: 'API Gateway',
          status: mockData.response_times.api < 200 ? 'healthy' : 'warning',
          response_time: mockData.response_times.api,
          uptime: 99.8,
          last_check: new Date().toISOString(),
        },
        {
          name: 'Cache',
          status: mockData.database.cache_hit_ratio > 80 ? 'healthy' : 'warning',
          response_time: 5,
          uptime: 99.9,
          last_check: new Date().toISOString(),
        },
        {
          name: 'Edge Functions',
          status: mockData.errors.rate < 1 ? 'healthy' : 'warning',
          response_time: 150,
          uptime: 99.7,
          last_check: new Date().toISOString(),
        },
      ];

      setServices(servicesData);

      // Add to realtime data
      const newDataPoint: RealtimeMetric = {
        timestamp: new Date().toISOString(),
        cpu: mockData.cpu,
        memory: mockData.memory,
        requests_per_minute: mockData.database.queries_per_second * 60,
        active_connections: mockData.database.connections,
        error_count: mockData.errors.total_5m,
      };

      setRealtimeData(prev => [newDataPoint, ...prev.slice(0, 59)]); // Keep last 60 points

    } catch (error) {
      productionLogger.error('Error fetching system health:', error);
      toast.error('Failed to fetch system health data');
    } finally {
      setLoading(false);
    }
  }, [generateMockSystemData]);

  // Set up real-time monitoring
  useEffect(() => {
    let performanceChannel: RealtimeChannel | null = null;

    const setupRealtime = async () => {
      try {
        // Monitor performance metrics for real-time system data
        performanceChannel = supabase
          .channel('system-performance-monitor')
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'performance_metrics'
            },
            (payload) => {
              productionLogger.info('Real-time performance metric:', payload.new);
              
              // Update system health based on new performance data
              const metric = payload.new as any;
              
              setSystemHealth(prev => ({
                ...prev,
                response_times: {
                  ...prev.response_times,
                  api: metric.metric_type === 'api_request' ? Number(metric.duration_ms) : prev.response_times.api,
                  database: metric.metric_type === 'database_query' ? Number(metric.duration_ms) : prev.response_times.database,
                },
                errors: {
                  ...prev.errors,
                  total_5m: metric.status === 'error' ? prev.errors.total_5m + 1 : prev.errors.total_5m,
                  rate: metric.status === 'error' ? Math.min(prev.errors.rate + 0.1, 100) : Math.max(prev.errors.rate - 0.05, 0),
                },
              }));
            }
          )
          .subscribe((status) => {
            setIsConnected(status === 'SUBSCRIBED');
            productionLogger.info('System monitor real-time connection:', status);
          });

      } catch (error) {
        productionLogger.error('Error setting up system monitoring:', error);
        setIsConnected(false);
      }
    };

    setupRealtime();
    fetchSystemHealth();

    // Update system data every 10 seconds
    const systemInterval = setInterval(fetchSystemHealth, 10000);

    return () => {
      if (performanceChannel) supabase.removeChannel(performanceChannel);
      clearInterval(systemInterval);
    };
  }, [fetchSystemHealth]);

  const getStatusColor = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'healthy': return 'text-primary';
      case 'warning': return 'text-accent';
      case 'error': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="h-4 w-4 text-primary" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-accent" />;
      case 'error': return <XCircle className="h-4 w-4 text-destructive" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const getProgressColor = (value: number, type: 'resource' | 'performance') => {
    if (type === 'resource') {
      return value > 80 ? 'bg-destructive' : value > 60 ? 'bg-accent' : 'bg-primary';
    } else {
      return value > 95 ? 'bg-primary' : value > 90 ? 'bg-accent' : 'bg-destructive';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="h-6 w-6" />
          <h2 className="text-2xl font-bold">System Monitor</h2>
          <Badge variant={isConnected ? "default" : "secondary"}>
            <Wifi className="h-3 w-3 mr-1" />
            {isConnected ? 'Connected' : 'Offline'}
          </Badge>
        </div>
        <Button onClick={fetchSystemHealth} variant="outline" size="sm">
          <Activity className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* System Resources */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Cpu className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">CPU</span>
              </div>
              <span className="text-sm font-bold">{systemHealth.cpu.toFixed(1)}%</span>
            </div>
            <Progress 
              value={systemHealth.cpu} 
              className="h-2"
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <HardDrive className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Memory</span>
              </div>
              <span className="text-sm font-bold">{systemHealth.memory.toFixed(1)}%</span>
            </div>
            <Progress 
              value={systemHealth.memory} 
              className="h-2"
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Disk</span>
              </div>
              <span className="text-sm font-bold">{systemHealth.disk.toFixed(1)}%</span>
            </div>
            <Progress 
              value={systemHealth.disk} 
              className="h-2"
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Network className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Network</span>
              </div>
              <span className="text-sm font-bold">{systemHealth.network.toFixed(1)}%</span>
            </div>
            <Progress 
              value={systemHealth.network} 
              className="h-2"
            />
          </CardContent>
        </Card>
      </div>

      {/* Services Status */}
      <Card>
        <CardHeader>
          <CardTitle>Service Health</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((service) => (
              <div key={service.name} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  {getStatusIcon(service.status)}
                  <div>
                    <p className="font-medium">{service.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {service.response_time.toFixed(0)}ms • {service.uptime}% uptime
                    </p>
                  </div>
                </div>
                <Badge variant={service.status === 'healthy' ? 'default' : service.status === 'warning' ? 'secondary' : 'destructive'}>
                  {service.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Detailed Metrics */}
      <Tabs defaultValue="database" className="space-y-4">
        <TabsList>
          <TabsTrigger value="database">Database</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="errors">Errors</TabsTrigger>
        </TabsList>

        <TabsContent value="database">
          <Card>
            <CardHeader>
              <CardTitle>Database Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-sm font-medium text-muted-foreground">Active Connections</p>
                  <p className="text-2xl font-bold">{systemHealth.database.connections}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-muted-foreground">Queries/sec</p>
                  <p className="text-2xl font-bold">{systemHealth.database.queries_per_second}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-muted-foreground">Avg Query Time</p>
                  <p className="text-2xl font-bold">{systemHealth.database.avg_query_time.toFixed(0)}ms</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-muted-foreground">Cache Hit Ratio</p>
                  <p className="text-2xl font-bold text-primary">{systemHealth.database.cache_hit_ratio.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <Card>
            <CardHeader>
              <CardTitle>Response Times</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-sm font-medium text-muted-foreground">API Endpoints</p>
                  <p className="text-2xl font-bold">{systemHealth.response_times.api.toFixed(0)}ms</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-muted-foreground">Database Queries</p>
                  <p className="text-2xl font-bold">{systemHealth.response_times.database.toFixed(0)}ms</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-muted-foreground">Static Assets</p>
                  <p className="text-2xl font-bold">{systemHealth.response_times.static.toFixed(0)}ms</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="errors">
          <Card>
            <CardHeader>
              <CardTitle>Error Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-sm font-medium text-muted-foreground">Errors (5 min)</p>
                  <p className="text-2xl font-bold text-destructive">{systemHealth.errors.total_5m}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-muted-foreground">Error Rate</p>
                  <p className="text-2xl font-bold text-destructive">{systemHealth.errors.rate.toFixed(2)}%</p>
                </div>
              </div>
              {systemHealth.errors.last_error && (
                <div className="mt-4 p-3 rounded-lg bg-destructive/10">
                  <p className="text-sm font-medium text-destructive">Last Error</p>
                  <p className="text-sm text-destructive">{systemHealth.errors.last_error}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}